import axios from 'axios';
import { IfNever } from 'type-fest';
import { config } from 'config';

/**
 * Params for making an API request, allowing optional request payload and AbortSignal.
 * Useful for structured parameters, f.x. when useMutation fn expects more than one argument.
 *
 * @template Payload - The type of the payload expected for the API request. If not specified, the `payload` property disappears.
 * @template PayloadName - Default `payload`. The name of the payload property to make it more meaningful.
 *
 * @example
 *
 * /// 1. the named payload is a scalar number property:
 *
 *   function fetchCampaign({ campaignId, signal }: ApiRequestOptions<number, 'campaignId'>)
 *   ...
 *   await fetchCampaign({ campaignId, signal });
 *
 * /// 2. the payload is an object:
 *
 *   function updateCampaign({ payload, signal }: ApiRequestOptions<UpdateCampaignRequest>)
 *   ...
 *   await updateCampaign({ payload, signal });
 */
export type ApiRequestParams<Payload = never, PayloadName extends string = 'payload'> = {
  /** Payload to be sent with the request */
  [P in IfNever<Payload, never, PayloadName>]: Payload;
} & {
  /** Optional `AbortSignal` to allow aborting the request */
  signal?: AbortSignal;
};

const basicAxiosConfig = {
  baseURL: config.api.baseUrl,
  timeout: 8000,
  headers: {},
  withCredentials: true,
};

export const axiosInstance = axios.create(basicAxiosConfig);

const tokenSearchParam = new URLSearchParams(window.location.search).get('secret');

export const axiosSecretAuthInstance = (tokenArg?: string) =>
  axios.create({
    ...basicAxiosConfig,
    headers: { Authorization: `Bearer ${tokenArg ?? tokenSearchParam}` },
  });
